import {
  IMultipleRowActions,
  UsePrivileges,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { AxiosResponse } from "axios";
import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IReprocessNonLabWorklistResult } from "../../../../services/nonLabWorklistReprocess.service";

export const isNonLabErrorStatus = (status?: string | null) =>
  (status ?? "").toUpperCase() === "ERRO";

const getSelectedIdentifiers = (api: MutableRefObject<GridApiCommunity>) => {
  const selectedRows = api.current?.getSelectedRows?.();

  if (!selectedRows) return [];

  return Array.from(selectedRows.values())
    .map((row) => Number(row.identifier))
    .filter((identifier) => !Number.isNaN(identifier) && identifier > 0);
};

interface IUseNonLabReprocessProps {
  translationPath: string;
  domainName: string;
  reprocess: (
    identifiers: number[],
  ) => Promise<AxiosResponse<IReprocessNonLabWorklistResult[]>>;
  onDone: () => void;
}

export const useNonLabReprocess = ({
  translationPath,
  domainName,
  reprocess,
  onDone,
}: IUseNonLabReprocessProps) => {
  const { t } = useTranslation();
  const { setMessage } = useNotificationContext();
  const [reprocessing, setReprocessing] = useState(false);
  const { privileges } = UsePrivileges();

  const canReprocess = useMemo(
    () =>
      privileges.some(
        (privilege) =>
          privilege.domainName === "all" ||
          (privilege.domainName === domainName &&
            (privilege.actions?.includes("Update") ||
              privilege.actions?.includes("All"))),
      ),
    [domainName, privileges],
  );

  const runReprocess = useCallback(
    async (identifiers: number[]) => {
      if (reprocessing) return;

      if (identifiers.length === 0) {
        setMessage({
          message: t(translationPath + "reprocess_select_rows"),
          type: "error",
        });
        return;
      }

      setReprocessing(true);

      try {
        const { data } = await reprocess(identifiers);
        const results = data ?? [];
        const reprocessedCount = results.filter((r) => r.reprocessed).length;
        const firstFailure = results.find((r) => !r.reprocessed);

        onDone();

        if (!firstFailure) {
          setMessage({
            message: t(translationPath + "reprocess_success", {
              count: reprocessedCount,
            }),
            type: "success",
          });
          return;
        }

        setMessage({
          message: t(
            translationPath +
              (reprocessedCount > 0 ? "reprocess_partial" : "reprocess_error"),
            {
              count: reprocessedCount,
              total: results.length,
              identifier: firstFailure.identifier,
              message: firstFailure.message ?? "",
            },
          ),
          type: reprocessedCount > 0 ? "warning" : "error",
        });
      } catch {
        setMessage({
          message: t(translationPath + "reprocess_request_error"),
          type: "error",
        });
      } finally {
        setReprocessing(false);
      }
    },
    [onDone, reprocess, reprocessing, setMessage, t, translationPath],
  );

  const reprocessRowAction = useCallback(
    (row: { identifier: number; status?: string | null }) =>
      canReprocess && isNonLabErrorStatus(row.status)
        ? {
            action: () => {
              runReprocess([row.identifier]);
            },
            icon: <ReplayIcon fontSize="small" />,
            text: t(translationPath + "reprocess") || "Reprocessar",
          }
        : null,
    [canReprocess, runReprocess, t, translationPath],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () =>
      canReprocess
        ? [
            {
              action: async (_, api) => {
                const identifiers = getSelectedIdentifiers(api);
                await runReprocess(identifiers);
                if (identifiers.length > 0)
                  api.current.setRowSelectionModel([]);
              },
              icon: <ReplayIcon fontSize="small" />,
              text: t(translationPath + "reprocess") || "Reprocessar",
            },
          ]
        : [],
    [canReprocess, runReprocess, t, translationPath],
  );

  return {
    canReprocess,
    reprocessing,
    reprocessRowAction,
    multipleRowsActions,
  };
};
